// src/services/auth.js
// Thin wrapper around your auth endpoints, robust to both token & cookie-session flows.

import api, {
  setAuthToken,
  clearAuthToken,
  getAuthToken,
  ApiError,
} from "./api";

// --- Local user cache key (optional) ---
const USER_KEY = "auth_user";

/** Persist the authenticated user locally (optional convenience) */
function cacheUser(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    // ignore storage errors
  }
}

/** Read cached user quickly (useful to render immediately) */
export function getCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** True if we appear logged-in (token exists OR we have a cached user from a cookie session) */
export function isAuthenticated() {
  return Boolean(getAuthToken() || getCachedUser());
}

// --- Helpers ---

/** Pull token from many common response shapes */
function extractToken(res) {
  if (!res) return null;
  if (typeof res === "string") {
    // Sometimes APIs return the JWT as a raw string
    return res.startsWith("eyJ") ? res : null;
  }
  return (
    res.token ||
    res.accessToken ||
    res.access_token ||
    res.jwt ||
    res.id_token ||
    res?.data?.token ||
    res?.data?.accessToken ||
    res?.data?.jwt ||
    res?.data?.id_token ||
    null
  );
}

/** Try to read user object from typical places */
function extractUser(res) {
  return res?.user ?? res?.data?.user ?? null;
}

// --- API calls ---

/** Register a new user.
 *  payload = { name, email, password }
 *  Returns: server-defined shape
 */
export async function register(payload) {
  if (!payload?.name || !payload?.email || !payload?.password) {
    throw new ApiError(400, "name, email and password are required");
  }
  // Include credentials in case backend also sets a session cookie on register
  return api.post("auth/register", payload, { auth: false, credentials: "include" });
}

/** Login with email/password.
 *  payload = { email, password }
 *  Returns: { user, token|null }
 *  Side effects: saves token via setAuthToken (if present), caches user locally
 */
export async function login(payload) {
  if (!payload?.email || !payload?.password) {
    throw new ApiError(400, "email and password are required");
  }

  // 1) Attempt login; allow cookies so session-based auth works
  const res = await api.post("auth/login", payload, { auth: false, credentials: "include" });

  // 2) Token-based success?
  const token = extractToken(res);
  let user = extractUser(res);

  if (token) {
    setAuthToken(token);
    if (!user) {
      // Fetch server-truth user if not included in login response
      try {
        const me = await fetchMe();
        user = me || null;
      } catch {
        // ignore; token is still saved
      }
    }
    cacheUser(user || null);
    return { user: user || null, token };
  }

  // 3) Cookie-session fallback: verify via /auth/me
  try {
    const me = await api.get("auth/me", undefined, { credentials: "include" });
    cacheUser(me || null);
    // No token saved; subsequent calls that rely on cookies must pass credentials:'include'
    return { user: me || null, token: null };
  } catch (err) {
    // Likely CORS/credentials/SameSite cookie issue
    throw new ApiError(
      500,
      "Login succeeded but no token was returned and session cookie not detected. " +
        "If your API uses cookies, enable CORS with credentials and set SameSite=None; Secure."
    );
  }
}

/** Logout (client-side).
 *  If your backend has /auth/logout to invalidate cookies or server sessions, set callServer=true.
 */
export async function logout({ callServer = false } = {}) {
  try {
    if (callServer) {
      // Uncomment if your API supports it:
      // await api.post("auth/logout", undefined, { credentials: "include" });
    }
  } finally {
    clearAuthToken();
    cacheUser(null);
  }
}

/** Fetch the current user from the API (server truth).
 *  GET /auth/me  →  { id, name, email, ... }
 *  Also refresh the local cache with the result.
 */
export async function fetchMe() {
  // Include credentials so it works for cookie-session backends too
  const me = await api.get("auth/me", undefined, { credentials: "include" });
  cacheUser(me || null);
  return me;
}

/** Convenience: sign up then log in automatically.
 *  payload = { name, email, password }
 *  Returns { user, token|null }
 */
export async function signupAndLogin(payload) {
  await register(payload);
  return login({ email: payload.email, password: payload.password });
}

// (Optional) Add updateProfile / password reset endpoints here if your backend exposes them.
